import { useState, useMemo } from 'react';
import { Card } from '../../../../layout/Card.jsx';
import { Icon } from '../../../../layout/Icon.jsx';
import { Pill } from '../../../../layout/Pill.jsx';
import {
  useRevenueCategories,
  useCreateRevenueCategory,
  useUpdateRevenueCategory,
  useDeleteRevenueCategory
} from '../../hooks/useRevenueCategories.js';
import { CategoryForm } from './CategoryForm.jsx';

function buildTree(items) {
  const byParent = {};
  items.forEach((c) => {
    const parent = c.parentId || c.parent?._id || 'root';
    if (!byParent[parent]) byParent[parent] = [];
    byParent[parent].push(c);
  });
  return byParent;
}

function CategoryNode({ cat, byParent, level, onEdit, onDelete }) {
  const id = cat._id || cat.id;
  const children = byParent[id] || [];
  return (
    <div>
      <div
        className="flex items-center gap-2 py-2 px-2 rounded-[8px] hover:bg-paper-2"
        style={{ paddingInlineStart: 8 + level * 16 }}
      >
        <span className="w-3 h-3 rounded-[4px] flex-none" style={{ background: cat.color || '#22C55E' }} />
        <span className="font-semibold text-[13px] flex-1 truncate">{cat.name}</span>
        {cat.isAuto ? <Pill variant="muted">تلقائية</Pill> : null}
        {cat.isActive === false ? <Pill variant="muted">معطّلة</Pill> : null}
        {cat.isAuto ? (
          <span className="text-ink-3" title="مغلقة"><Icon name="cashbox" size={13} /></span>
        ) : (
          <div className="flex items-center gap-1">
            <button
              type="button"
              className="btn btn-ghost"
              style={{ padding: 4 }}
              onClick={() => onEdit(cat)}
            >
              <Icon name="edit" size={13} />
            </button>
            <button
              type="button"
              className="btn btn-ghost"
              style={{ padding: 4, color: '#EF4444' }}
              onClick={() => onDelete(cat)}
            >
              <Icon name="trash" size={13} />
            </button>
          </div>
        )}
      </div>
      {children.map((c) => (
        <CategoryNode
          key={c._id || c.id}
          cat={c}
          byParent={byParent}
          level={level + 1}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      ))}
    </div>
  );
}

export function RevenueCategoriesPanel() {
  const { data: categories = [], isLoading } = useRevenueCategories();
  const create = useCreateRevenueCategory();
  const update = useUpdateRevenueCategory();
  const remove = useDeleteRevenueCategory();

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const byParent = useMemo(() => buildTree(categories), [categories]);
  const roots = byParent.root || [];

  function openCreate() {
    setEditing(null);
    setFormOpen(true);
  }
  function openEdit(c) {
    setEditing(c);
    setFormOpen(true);
  }
  function closeForm() {
    setFormOpen(false);
    setEditing(null);
  }

  async function handleSubmit(body) {
    if (editing) {
      await update.mutateAsync({ id: editing._id || editing.id, body }, { onSuccess: () => closeForm() });
    } else {
      await create.mutateAsync(body, { onSuccess: () => closeForm() });
    }
  }

  return (
    <Card
      header="فئات الإيرادات"
      sub="فئات إضافية للإيرادات (الفئات التلقائية للقراءة فقط)"
    >
      <div className="flex justify-end mb-2">
        <button type="button" className="btn btn-primary" onClick={openCreate}>
          <Icon name="plus" size={14} /> فئة
        </button>
      </div>
      {isLoading ? (
        <div className="text-ink-3 text-center py-6">جاري التحميل...</div>
      ) : roots.length === 0 ? (
        <div className="text-ink-3 text-center py-6">لا توجد فئات بعد</div>
      ) : (
        <div className="flex flex-col">
          {roots.map((c) => (
            <CategoryNode
              key={c._id || c.id}
              cat={c}
              byParent={byParent}
              level={0}
              onEdit={openEdit}
              onDelete={(x) => setConfirmDelete(x)}
            />
          ))}
        </div>
      )}

      <CategoryForm
        open={formOpen}
        category={editing}
        parents={categories.filter((c) => !c.isAuto)}
        onClose={closeForm}
        onSubmit={handleSubmit}
        submitting={create.isPending || update.isPending}
        kind="revenue"
      />

      {confirmDelete ? (
        <div
          className="fixed inset-0 z-50 grid place-items-center"
          style={{ background: 'rgba(0,0,0,.45)' }}
        >
          <div className="card p-5 w-[420px] space-y-3" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-[15px] font-bold">حذف الفئة</h3>
            <div className="text-[13px] text-ink-2">
              هل تريد حذف الفئة "{confirmDelete.name}"؟
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" className="btn" onClick={() => setConfirmDelete(null)} disabled={remove.isPending}>
                إلغاء
              </button>
              <button
                type="button"
                className="btn btn-danger"
                disabled={remove.isPending}
                onClick={async () => {
                  await remove.mutateAsync(confirmDelete._id || confirmDelete.id, {
                    onSuccess: () => setConfirmDelete(null)
                  });
                }}
              >
                {remove.isPending ? 'جاري...' : 'حذف'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </Card>
  );
}
